import type { CodeSessionPullRequest } from '@/lib/code-sessions/pull-request';
import type { CodeSessionPerson, CodeSessionProfile } from '@/lib/code-sessions/session-people';
import type { QueryClient } from '@tanstack/react-query';
import { infiniteQueryOptions, queryOptions, replaceEqualDeep } from '@tanstack/react-query';

import type { CodeRepositoryRef } from './environment';
import type {
  CodeSessionSummary,
  CodeSessionSummaryFields,
  CodeSessionTurn,
  SessionResponse,
} from './types';

import { cloudRuntime, codeSessionRequest } from './runtime/cloud';
import { listLocalSessions } from './runtime/local/local-runtime';
import { isRefusal } from './runtime/request-error';
import { codeRuntimeOf, runtimeFor } from './runtime/runtime-for';
import { userMessageText } from './turn-prompt';

import { mergeSetupEvents } from '@/lib/code-sessions/setup-events';
import { isTerminalTurnStatus, isUndeliveredTurnStatus } from '@/lib/code-sessions/turn-utils';
import { defaultQueryRetry } from '@/lib/query-retry';

export const codeSessionKeys = {
  lists: ['code-sessions'] as const,
  access: ['code-sessions-access'] as const,
  archive: ['code-sessions', 'archive'] as const,
  unarchive: ['code-sessions', 'unarchive'] as const,
  rename: ['code-sessions', 'rename'] as const,
  regenerateTitle: ['code-sessions', 'regenerate-title'] as const,
  create: ['code-sessions', 'create'] as const,
  fork: ['code-sessions', 'fork'] as const,
  list: (installationId: number) => ['code-sessions', installationId] as const,
  repositoryList: (installationId: number) =>
    ['code-session-repositories', installationId] as const,
  archivedLists: ['code-sessions-archived'] as const,
  archived: (installationIds: readonly number[], query: string) =>
    ['code-sessions-archived', installationIds, query] as const,
  // Sessions on the person's own computer, outside the `lists` prefix and never saved in the
  // browser: cubic's server has no record of them, and an older desktop bundle must not read them.
  local: ['code-sessions-local'] as const,
  // Outside the `lists` prefix: every query under it is read as session rows.
  detail: (sessionId: string) => ['code-session', sessionId] as const,
  savedTurns: (sessionId: string) => ['code-session-turns', sessionId] as const,
  sideAgents: (sessionId: string) => ['code-session-side-agents', sessionId] as const,
  warmBoot: (sessionId: string) => ['code-session-warm-boot', sessionId] as const,
  mutation: (sessionId: string, action: string) => ['code-session', sessionId, action] as const,
};

/**
 * The list row for a session row. List reads pass the rows they receive through it too; those are
 * typed as list rows, so the creator ID is optional here.
 */
export function toSessionSummary(
  row: CodeSessionSummaryFields &
    Pick<CodeSessionSummary, 'createdByProfileId' | 'checkInDueAt' | 'agentsAtWork'>,
  pullRequest: CodeSessionPullRequest | null,
  // Absent after failed creator reads, from a row the client made itself, and from older servers' lists.
  createdBy?: CodeSessionProfile | null,
): CodeSessionSummary {
  const {
    id,
    installationId,
    repositoryId,
    title,
    repoFullName,
    sessionBranch,
    status,
    sandboxStatus,
    lastActivityAt,
    updatedAt,
    forkedFromSessionId,
    startedBySessionId,
    source,
    sourceUrl,
    mcpClientName,
    rewindTurnId,
    awaitingInputAt,
    createdByProfileId,
    checkInDueAt,
    agentsAtWork,
  } = row;
  return {
    id,
    installationId,
    repositoryId,
    title,
    repoFullName,
    sessionBranch,
    status,
    sandboxStatus,
    lastActivityAt,
    updatedAt,
    forkedFromSessionId,
    startedBySessionId,
    source,
    sourceUrl,
    mcpClientName,
    rewindTurnId,
    awaitingInputAt,
    pullRequest,
    ...(createdBy === undefined ? {} : { createdBy }),
    // Left out when absent, as `createdBy` is, so the row deep-equals its saved copy, which
    // drops undefined values.
    ...(createdByProfileId === undefined ? {} : { createdByProfileId }),
    // A newer cached row keeps its own check-in and takes the agents of this one (`mergeSessionSummaries`).
    ...(checkInDueAt === undefined ? {} : { checkInDueAt }),
    ...(agentsAtWork === undefined ? {} : { agentsAtWork }),
  };
}

/** Where the sidebar lists a session, which its time does not always read. */
export function listPlace(row: Pick<CodeSessionSummary, 'placedAt' | 'lastActivityAt'>): string {
  return row.placedAt ?? row.lastActivityAt;
}

function isWorkingRow(row: Pick<CodeSessionSummary, 'status'>): boolean {
  return row.status === 'running' || row.status === 'creating';
}

function placedAt(
  existing: CodeSessionSummary | undefined,
  next: Pick<CodeSessionSummary, 'lastActivityAt'>,
): string | undefined {
  if (existing === undefined) {
    return undefined;
  }
  const place =
    !isWorkingRow(existing) && Date.parse(next.lastActivityAt) > Date.parse(existing.lastActivityAt)
      ? next.lastActivityAt
      : listPlace(existing);
  return place === next.lastActivityAt ? undefined : place;
}

export function mergeSessionSummaries(
  previous: CodeSessionSummary[] | undefined,
  incoming: CodeSessionSummary[],
): CodeSessionSummary[] {
  const saved = new Map(previous?.map((row) => [row.id, row]));
  const merged = incoming.map((row) => {
    const existing = saved.get(row.id);
    // A pull request's changes leave the session's version alone, and every
    // list read observes the mirror anew. So it does the agents at work on the
    // session's tasks; the check-in is the session's own, and stays with its version.
    const {
      agentsAtWork: _agentsAtWork,
      placedAt: _placedAt,
      ...next
    } = existing && existing.updatedAt > row.updatedAt
      ? { ...existing, pullRequest: row.pullRequest }
      : row;
    // Who started a session never changes, so a row saved without its creator, as by a window
    // on older code, takes the one the other names.
    const createdBy = row.createdBy === undefined ? existing?.createdBy : row.createdBy;
    const createdByProfileId = row.createdByProfileId ?? existing?.createdByProfileId;
    const place = placedAt(existing, next);
    return replaceEqualDeep(existing, {
      ...next,
      ...(row.agentsAtWork === undefined ? {} : { agentsAtWork: row.agentsAtWork }),
      ...(createdBy === undefined ? {} : { createdBy }),
      ...(createdByProfileId === undefined ? {} : { createdByProfileId }),
      ...(place === undefined ? {} : { placedAt: place }),
    });
  });
  // A read that changes nothing keeps the previous list and rows, so no row redraws.
  return merged.length === previous?.length && merged.every((row, index) => row === previous[index])
    ? previous
    : merged;
}

/** The app's default retry, but never after a refusal: the server would refuse it again. */
function retryUnlessRefused(failureCount: number, error: Error): boolean {
  return !isRefusal(error) && defaultQueryRetry(failureCount, error);
}

/**
 * `enabled` for a read that polls or refetches on its own: once the server
 * refuses it, it stops. Without data, an errored query otherwise refetches on
 * every interval, remount, tab focus, and invalidation. Writing its data or an
 * explicit `refetch()` that succeeds re-enables it.
 */
export function enabledUntilRefused(enabled = true) {
  return (query: { state: { error: Error | null } }) => enabled && !isRefusal(query.state.error);
}

export function mergeSavedTurn(
  previous: CodeSessionTurn | undefined,
  incoming: CodeSessionTurn,
): CodeSessionTurn {
  if (!previous) {
    return incoming;
  }
  // A send its runtime never got did not run: once a copy reaches the runtime, late or sent again,
  // that copy's row is the turn.
  if (previous.unsent && !incoming.unsent) {
    return incoming;
  }
  // Turns do not restart: a late snapshot/POST cannot undo execution or completion.
  const preserveStatus =
    (isTerminalTurnStatus(previous.status) && !isTerminalTurnStatus(incoming.status)) ||
    (previous.status === 'admitted' && incoming.status === 'queued') ||
    (previous.status === 'running' && isUndeliveredTurnStatus(incoming.status));
  return {
    ...previous,
    ...incoming,
    ...(preserveStatus
      ? {
          status: previous.status,
          inboxId: previous.inboxId,
          endedAt: previous.endedAt,
          errorMessage: previous.errorMessage,
          changedFiles: previous.changedFiles,
        }
      : {}),
    startedAt: previous.startedAt ?? incoming.startedAt,
    delivery: previous.delivery === 'steer' ? 'steer' : incoming.delivery,
    // A committed rewind is final, whatever the status: a read that started
    // before it cannot bring the turn back.
    ...(previous.discardedAt ? { discardedAt: previous.discardedAt } : {}),
    // So is a send's claim: a read that started before it cannot make the
    // prompt editable again.
    ...(previous.admissionAttemptedAt
      ? { admissionAttemptedAt: previous.admissionAttemptedAt }
      : {}),
  };
}

/**
 * The newest row's successful complete people list is authoritative, including removals.
 * An undefined list from a failed read falls back to the other successful list.
 */
function mergeSessionPeople(
  previous: CodeSessionPerson[] | undefined,
  incoming: CodeSessionPerson[] | undefined,
  incomingIsOlder: boolean,
): CodeSessionPerson[] | undefined {
  return incomingIsOlder ? (previous ?? incoming) : (incoming ?? previous);
}

/**
 * A prompt commits a waiting rewind. A runtime whose agent deletes the turns a commit discards
 * (OpenCode on the person's computer) shows the commit only in a later read, one with no rewind
 * that no longer lists the rewind's turn. That turn and every later one the read lacks stay hidden,
 * discarded, as a cloud read keeps them. A cloud read lists every turn, so it never meets this.
 */
function discardCommittedTurns(
  turns: Map<string, CodeSessionTurn>,
  rewindTurnId: string | null,
  incoming: readonly CodeSessionTurn[],
) {
  const boundary = rewindTurnId === null ? undefined : turns.get(rewindTurnId);
  const listed = new Set(incoming.map((turn) => turn.id));
  if (boundary === undefined || listed.has(boundary.id)) {
    return;
  }
  const discardedAt = new Date().toISOString();
  for (const [id, turn] of turns) {
    if (turn.index >= boundary.index && !listed.has(id) && !turn.discardedAt) {
      turns.set(id, { ...turn, discardedAt });
    }
  }
}

/** Merge server content only. Live events and unconfirmed commands stay in the controller. */
export function mergeSessionSnapshot(
  previous: SessionResponse | undefined,
  incoming: SessionResponse,
): SessionResponse {
  if (!previous || previous.session.id !== incoming.session.id) {
    return incoming;
  }
  // Primary reads can still finish out of order. Keep newer row state while
  // merging transcript content and events, which have their own ordering.
  const current = previous.session.updatedAt > incoming.session.updatedAt ? previous : incoming;
  const turns = new Map(previous.session.turns.map((turn) => [turn.id, turn]));
  for (const turn of incoming.session.turns) {
    turns.set(turn.id, mergeSavedTurn(turns.get(turn.id), turn));
  }
  // On the person's computer a queued prompt exists only while OpenCode holds it: a newer read
  // that lacks one says it was taken or removed, so the row goes, or Remove and Steer would find
  // nothing. The page writes its own send ahead of a read that began before it, and that read
  // brings the row along (`sentSince`), so the row stays. The cloud page writes its own prompts
  // ahead of the read that confirms them, so its rows stay.
  if (current === incoming && codeRuntimeOf(incoming.session.id).queue === 'inbox') {
    const listed = new Set(incoming.session.turns.map((turn) => turn.id));
    for (const [id, turn] of turns) {
      if (turn.status === 'queued' && !listed.has(id)) {
        turns.delete(id);
      }
    }
  }
  if (current === incoming && incoming.session.rewindTurnId === null) {
    discardCommittedTurns(turns, previous.session.rewindTurnId, incoming.session.turns);
  }
  const messages = new Map(
    (previous.transcript?.messages ?? []).map((message) => [message.id, message]),
  );
  for (const message of incoming.transcript?.messages ?? []) {
    const saved = messages.get(message.id);
    if (message.type === 'user' && saved && userMessageText(saved) && !userMessageText(message)) {
      continue;
    }
    messages.set(message.id, message);
  }
  return {
    ...current,
    events: incoming.events,
    // Saved setup events only add: a read that failed to load them, or began before a save, cannot
    // take cards from the cache.
    setupEvents:
      incoming.setupEvents || previous.setupEvents
        ? mergeSetupEvents(previous.setupEvents, incoming.setupEvents ?? [])
        : undefined,
    cursor: incoming.cursor,
    // Read per viewer from the MCP servers' current state, not ordered by the row.
    mcpToolsRunAs: incoming.mcpToolsRunAs,
    // The subscription's owner never changes, so a response that leaves it out keeps the last answer.
    aiSubscription: incoming.aiSubscription ?? previous.aiSubscription,
    people: mergeSessionPeople(previous.people, incoming.people, current === previous),
    session: {
      ...current.session,
      turns: [...turns.values()].sort((a, b) => a.index - b.index),
      lastActivityAt:
        previous.session.lastActivityAt > incoming.session.lastActivityAt
          ? previous.session.lastActivityAt
          : incoming.session.lastActivityAt,
    },
    // An unavailable/resuming sandbox is not evidence that its transcript was deleted.
    transcript: incoming.transcript
      ? { ...incoming.transcript, messages: [...messages.values()] }
      : previous.transcript,
  };
}

// Hover prefetches still in flight, so opening the session waits for its read
// instead of starting a second one.
const sessionPrefetches = new Map<string, Promise<SessionResponse | null>>();
// When a session's prefetch last found nothing to cache. Rows can look ready
// long after Vercel stops an idle VM, so hovering one again must not re-read it.
const emptyPrefetchAt = new Map<string, number>();
const EMPTY_PREFETCH_TTL_MS = 60_000;

/**
 * Reads a session whose sandbox is running before its viewer opens it, so the
 * transcript is cached by the time they click. The prefetch read never wakes a
 * sandbox: a stopped one answers with no content and is left to the real open.
 */
export function prefetchCodeSession(client: QueryClient, sessionId: string): void {
  const key = codeSessionKeys.detail(sessionId);
  const emptyAt = emptyPrefetchAt.get(sessionId);
  if (
    client.getQueryData(key) !== undefined ||
    sessionPrefetches.has(sessionId) ||
    (emptyAt !== undefined && Date.now() - emptyAt < EMPTY_PREFETCH_TTL_MS)
  ) {
    return;
  }
  const read = runtimeFor(sessionId)
    .conversation.prefetch()
    .catch(() => null)
    .then((body) => {
      if (!body) {
        emptyPrefetchAt.set(sessionId, Date.now());
      } else if (client.getQueryData(key) === undefined) {
        client.setQueryData(key, body);
      }
      return body;
    })
    .finally(() => sessionPrefetches.delete(sessionId));
  sessionPrefetches.set(sessionId, read);
}

/** How often a session that is starting or resuming is read again. */
export const STARTING_SESSION_READ_MS = 2_000;

export function codeSessionOptions(sessionId: string) {
  return queryOptions({
    queryKey: codeSessionKeys.detail(sessionId),
    queryFn: async ({ signal }) =>
      (await sessionPrefetches.get(sessionId)) ??
      runtimeFor(sessionId).conversation.snapshot({ signal }),
    staleTime: 0,
    gcTime: 30 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    // Poll during resume so a missed realtime event cannot leave the UI loading.
    // Stop when recovery finishes or the request fails.
    refetchInterval: (query) =>
      query.state.status !== 'error' &&
      (query.state.data?.resuming || query.state.data?.session.status === 'creating')
        ? STARTING_SESSION_READ_MS
        : false,
    // Override the application's previous-query placeholder: another session is never a placeholder.
    placeholderData: undefined,
    structuralSharing: (previous, incoming) =>
      mergeSessionSnapshot(previous as SessionResponse | undefined, incoming as SessionResponse),
  });
}

/** The saved session and its turns, read without waking its sandbox the way the detail read does. */
export function codeSessionSavedTurnsOptions(sessionId: string) {
  return queryOptions({
    queryKey: codeSessionKeys.savedTurns(sessionId),
    queryFn: ({ signal }) => runtimeFor(sessionId).conversation.savedTurns({ signal }),
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    placeholderData: undefined,
  });
}

/** A session's side agents, newest first. Titles are fixed at creation, so creating one refetches. */
export function codeSideAgentsOptions(sessionId: string) {
  return queryOptions({
    queryKey: codeSessionKeys.sideAgents(sessionId),
    queryFn: ({ signal }) =>
      codeSessionRequest<{ agents: { id: string; title: string }[] }>(
        `/api/code-sessions/${sessionId}/side-agents`,
        { signal },
      ),
    // The app keeps the previous query's data by default; another session's agents never stand in.
    placeholderData: undefined,
  });
}

/** Installations where the signed-in user can use /code; empty means no access. */
export function codeSessionsAccessOptions() {
  return queryOptions({
    queryKey: codeSessionKeys.access,
    queryFn: async ({ signal }) => {
      const body = await codeSessionRequest<{ installationIds: number[] }>(
        '/api/code-sessions/access',
        { signal },
      );
      return body.installationIds;
    },
    staleTime: 5 * 60_000,
    // Saved in the browser with the lists it picks, so a reload shows them before this answers.
    meta: { persist: true },
  });
}

function codeSessionListDefaults() {
  return {
    // A refused read would be refused again: no retry, and no more fetching.
    retry: retryUnlessRefused,
    enabled: enabledUntilRefused(),
    placeholderData: undefined,
    staleTime: 30_000,
    gcTime: 30 * 60_000,
    // Saved in the browser: a reload shows the last list while this reads it again.
    meta: { persist: true },
  };
}

export function codeSessionRepositoryOptions(installationId: number) {
  return queryOptions({
    ...codeSessionListDefaults(),
    queryKey: codeSessionKeys.repositoryList(installationId),
    queryFn: async ({ signal }) => {
      const body = await codeSessionRequest<{ repositories: CodeRepositoryRef[] }>(
        `/api/code-sessions/repositories?installationId=${installationId}`,
        { signal },
      );
      return body.repositories;
    },
  });
}

/**
 * Whether a session list should be read again soon, in either runtime: one of its sessions works,
 * or agents still work on a task one sent, and each answers back as a turn of that session.
 */
function listChangesSoon(rows: readonly CodeSessionSummary[] | undefined): boolean {
  return rows?.some((row) => isWorkingRow(row) || row.agentsAtWork !== undefined) === true;
}

export function codeSessionListOptions(installationId: number) {
  return queryOptions({
    ...codeSessionListDefaults(),
    queryKey: codeSessionKeys.list(installationId),
    queryFn: async ({ signal }) => {
      const rows = await cloudRuntime.listSessions({ installationId }, { signal });
      return rows.map((row) => toSessionSummary(row, row.pullRequest, row.createdBy));
    },
    // Realtime covers only the open session. Poll while others work so their
    // finished turns (and unseen-activity dots) reach the sidebar.
    refetchInterval: (query) => (listChangesSoon(query.state.data) ? 5_000 : false),
    // Accept newer restores, but never let a slower pre-Undo read hide them.
    structuralSharing: (previous, incoming) =>
      mergeSessionSummaries(
        previous as CodeSessionSummary[] | undefined,
        incoming as CodeSessionSummary[],
      ),
  });
}

/**
 * The sessions on the person's own computer, read from its OpenCode server through the page's
 * connection, which starts the server first. Unlike the cloud lists it is not saved in the
 * browser: a window without the plugin could not read the rows again, and an older desktop
 * bundle must not meet rows it does not know. Nothing retries a failed read, since the read
 * may have started the server, and the sidebar's Retry starts it again on purpose: not a
 * focus, and not the browser coming back online, which says nothing of a server on loopback.
 */
export function localCodeSessionsOptions(enabled: boolean) {
  return queryOptions({
    queryKey: codeSessionKeys.local,
    queryFn: ({ signal }) => listLocalSessions({ signal }),
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 30_000,
    placeholderData: undefined,
    // A poll that reads the same sessions keeps the rows the sidebar holds, so it draws nothing
    // again; the app's default turns this off. A row the page wrote from a newer read of its
    // session (`localLists.update`) stays over the older row a list read begun before it brings.
    structuralSharing: (previous, incoming) =>
      mergeSessionSummaries(
        previous as CodeSessionSummary[] | undefined,
        incoming as CodeSessionSummary[],
      ),
    // The page follows only the open session's work, and every session's asks (`connection.ts`), so
    // poll while another one works, or waits on its subagents, until OpenCode wakes it.
    refetchInterval: (query) =>
      query.state.status !== 'error' && listChangesSoon(query.state.data) ? 5_000 : false,
  });
}

/** Each installation's cursor to its next page of archived sessions; one without a cursor has no more. */
type ArchivedCursors = Record<number, string>;

export type ArchivedSessionsPage = { sessions: CodeSessionSummary[]; next: ArchivedCursors };

const ARCHIVED_PAGE_SIZE = 50;

/** Archived sessions across installations, paged, optionally matching `query`. */
export function archivedCodeSessionsOptions(
  installationIds: readonly number[],
  query: string,
  enabled = true,
) {
  return infiniteQueryOptions({
    queryKey: codeSessionKeys.archived(installationIds, query),
    queryFn: async ({ pageParam, signal }): Promise<ArchivedSessionsPage> => {
      const targets =
        pageParam === null
          ? installationIds.map((installationId) => ({ installationId, cursor: undefined }))
          : Object.entries(pageParam).map(([installationId, cursor]) => ({
              installationId: Number(installationId),
              cursor,
            }));
      // One installation refusing its archive leaves the others listed, as in the session list.
      // Any other failure fails the page so it can be retried.
      const settled = await Promise.allSettled(
        targets.map(async ({ installationId, cursor }) => {
          const params = new URLSearchParams({
            installationId: String(installationId),
            limit: String(ARCHIVED_PAGE_SIZE),
            q: query,
            ...(cursor ? { cursor } : {}),
          });
          const body = await codeSessionRequest<{
            sessions: CodeSessionSummary[];
            nextCursor: string | null;
          }>(`/api/code-sessions/archived?${params}`, { signal });
          return { installationId, ...body };
        }),
      );
      const pages = settled.flatMap((result) =>
        result.status === 'fulfilled' ? [result.value] : [],
      );
      const failures = settled.filter(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      );
      const failure =
        failures.find((result) => !isRefusal(result.reason)) ??
        (pages.length === 0 ? failures[0] : undefined);
      if (failure) {
        throw failure.reason;
      }
      return {
        sessions: pages.flatMap((page) =>
          page.sessions.map((row) => toSessionSummary(row, row.pullRequest, row.createdBy)),
        ),
        next: Object.fromEntries(
          pages.flatMap((page) =>
            page.nextCursor ? [[page.installationId, page.nextCursor]] : [],
          ),
        ),
      };
    },
    initialPageParam: null as ArchivedCursors | null,
    getNextPageParam: (last) => (Object.keys(last.next).length > 0 ? last.next : undefined),
    retry: retryUnlessRefused,
    enabled: enabledUntilRefused(enabled && installationIds.length > 0),
    staleTime: 30_000,
    // A search over the same installations keeps the last rows until its own
    // arrive; the hook filters them by the new search. The whole archive never
    // stands in with a search's rows.
    placeholderData: (previous, previousQuery) =>
      query !== '' && previousQuery?.queryKey[1].join() === installationIds.join()
        ? previous
        : undefined,
  });
}
